import "../../../App.scss";
import * as React from "react";
import { JSX, ReactNode } from "react";
import IbssFooter from '../../../Components/Layout/Footer/IbssFooter';
import { appContext } from "../../../AppContext";
import { OktaAuth } from "@okta/okta-auth-js";
import LegacyApiClient from "../../../Providers.Api/LegacyApiClient";
import { ReactComponent as EmailIcon } from './EmailEnvelope.svg';
import { ReactComponent as LockIcon } from './PasswordLock.svg';
import LoginSplash from './fadx-login-page.jpeg';
import { IAuthenticationResponse, IIdentityProviderData } from "./DataModels";
import { AlertDialog, IRouterProps, withRouting } from "@iconics/components";
import { IbssComponent } from "../../../Components/Core/BaseComponent/IbssComponent";
import { SecurityProviderEnum } from "../../../Providers.LocalStorage/Models";
import "./Styles.scss";
import { Box, Button, ButtonBar, Card, Dialog, DialogContent, DialogFooter, GridContainer, GridItem, PasswordField, RightButtons, ScreenSize, TextField, Typography } from "@iconics/components";
import { StringHelper } from "@iconics/core";

export class Login extends IbssComponent<IProps, IState>
{
    private labels = appContext().labels;
    private boxRef: React.RefObject<Box | null>;
    private readonly emailPattern = new RegExp(/^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i);
    private get screenSize(): ScreenSize { return window.innerWidth < 1052 ? 'compact' : 'expanded'; }

    private readonly resizeListener = () => this.handleResize();
    private readonly handleResize = (): void =>
    {
        const newSize = this.screenSize;
        if (this.state.screenSize !== newSize)
        {
            this.setState({ screenSize: newSize });
        }
    };

    constructor(props: IProps)
    {
        super(props);
        this.boxRef = React.createRef();

        this.state =
        {
            email: '',
            emailError: '',
            showPassword: false,
            password: '',
            passwordError: '',
            panel: 'login',
            identityProviders: [],
            showPrivacyPolicyDialog: false,
            enablePrivacyPolicyButtons: false,
            privacyPolicyContent: "",
            screenSize: this.screenSize,
            showLoginFailed: false,
            showResetPasswordResult: false,
            resetPasswordMessage: '',
        };
    }

    public async componentDidMount(): Promise<void>
    {
        window.addEventListener('resize', this.resizeListener);
        await appContext().authentication.clearStorage();
        if (!appContext().localStorageProvider.getUserPolicy())
        {
            await this.loadPrivacyPolicy();
        }
    }

    public componentWillUnmount(): void
    {
        window.removeEventListener('resize', this.resizeListener);
    }

    public componentDidUpdate(prevProps: IProps, prevState: IState): void
    {
        if (!prevState.showPrivacyPolicyDialog && this.state.showPrivacyPolicyDialog)
        {
            requestAnimationFrame(() => this.handlePrivacyPolicyOpen());
        }
    }

    private async loadPrivacyPolicy(): Promise<void>
    {
        try
        {
            const response = await LegacyApiClient.getPrivacyPolicy();
            //const string = new StringHelper();
            //const response = string.generateRandomWords(1000);

            if (response?.length > 0)
            {
                this.setState({ privacyPolicyContent: response, showPrivacyPolicyDialog: true });
            }
        }
        catch (error)
        {
            this.setState({ showPrivacyPolicyDialog: false });
        }
    }

    private validateEmail(email: string): boolean
    {
        if (!email)
        {
            this.setState({ emailError: this.labels.Platform_Login_EnterEmailMessage });
            return false;
        }
        else if (!this.emailPattern.test(email))
        {
            this.setState({ emailError: this.labels.Platform_Login_IncorrectEmailError });
            return false;
        }
        else
        {
            this.setState({ emailError: '' });
            return true;
        }
    }

    private validatePassword(password: string): boolean
    {
        if (!password)
        {
            this.setState({ passwordError: this.labels.Platform_Login_EnterPasswordMessage });
            return false;
        }
        else 
        {
            this.setState({ passwordError: '' });
            return true;
        }
    }

    public handlePrivacyPolicyOpen(): void 
    {
        this.setState({ enablePrivacyPolicyButtons: false });
        const boxElement = this.boxRef.current?.ref.current;
        if (!boxElement)
        {
            return;
        }
        if (boxElement.scrollHeight <= boxElement.clientHeight)
        {
            this.setState({ enablePrivacyPolicyButtons: true });
        }
    }

    private handlePrivacyPolicyScroll(e: React.UIEvent<HTMLDivElement>): void 
    {
        const hasScrolledToBottom = (e.currentTarget.scrollHeight - e.currentTarget.clientHeight <= e.currentTarget.scrollTop + 20);
        if (hasScrolledToBottom) 
        {
            this.setState({ enablePrivacyPolicyButtons: true });
        }
    }

    private handlePrivacyPolicyDecline(): void
    {
        window.location.href = "https://www.intelligentbuildingssoftware.com/";
    }

    private handlePrivacyPolicyAccept(): void
    {
        appContext().localStorageProvider.setUserPolicy(true);
        this.setState({ showPrivacyPolicyDialog: false });
    }

    private handleEmailChange(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void
    {
        const { value } = event.target;
        this.setState({ email: value, showPassword: false });
        this.validateEmail(value);

        if (this.state.panel == 'forgotten-password')
        {
            return;
        }
        else
        {
            this.setState({ panel: 'login' });
        }
    }

    private handlePasswordChange(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void
    {
        const { value } = event.target;
        this.setState({ password: value });
        this.validatePassword(value);
    }

    private async handleLoginClick(): Promise<void>
    {
        const isEmailValid = this.validateEmail(this.state.email);
        if (!isEmailValid)
        {
            return;
        }

        try
        {
            const userIdentityProviders = await appContext().fadxPlatformApiClientV2.v2.security.users.getIdentityProvider.post<IIdentityProviderData[]>({
                body: { username: this.state.email }
            });

            const identityProviders = userIdentityProviders
                .map(
                    identityProvider =>
                    ({
                        clientId: identityProvider.ClientId ?? '',
                        id: identityProvider.ID,
                        identityProviderName: identityProvider.Name ?? '',
                        securityProviderTypeId: identityProvider.SecurityProviderTypeId,
                        tenantDomain: identityProvider.TenantDomain ?? '',
                        tenancyId: identityProvider.TenancyId,
                    } as IIdentityProvider)
                )
                .sort((a, b) => (a.securityProviderTypeId ?? '').localeCompare(b.securityProviderTypeId ?? ''));

            await this.setStateAsync({ identityProviders: identityProviders });
        }
        catch
        {
            this.props.navigate('/out-of-service');
        }

        try
        {
            if (this.state.identityProviders.length === 1)
            {
                await this.login(this.state.identityProviders[0]);
            }
            else if (this.state.identityProviders.length > 1)
            {
                this.setState({ panel: 'identity-provider-selection' });
            }
            else
            {
                this.setState({ emailError: this.labels.Platform_Login_IncorrectEmailError });
            }
        }
        catch
        {
            return;
        }
    }

    private async handleIdentityProviderClick(identityProvider: IIdentityProvider): Promise<void>
    {
        await this.login(identityProvider);
    }

    private async login(identityProvider: IIdentityProvider): Promise<void>
    {
        appContext().tenantId = identityProvider.tenancyId;
        const user = appContext().localStorageProvider.getUserDetails();
        appContext().localStorageProvider.setUserDetails({
            ...user,
            userType: identityProvider.identityProviderName,
            providerType: identityProvider.securityProviderTypeId,
        })

        switch (identityProvider.securityProviderTypeId)
        {
            case 'Native':
                await this.setState({ showPassword: true });
                break;

            case 'AAD':
                await this.loginWithAad(identityProvider);
                break;

            case 'Okta':
                await this.loginWithOkta(identityProvider);
                break;

            case 'Auth0':
                await this.loginWithAuth0(identityProvider);
                break;

            default:
                this.setState({ showLoginFailed: true });
                break;
        }
    }

    private async loginWithAad(identityProvider: IIdentityProvider): Promise<void>
    {
        try
        {
            if (!identityProvider.clientId || !identityProvider.tenantDomain)
            {
                return;
            }

            const msal = appContext().msalProvider;
            await msal.initialise(identityProvider.clientId, identityProvider.tenantDomain);
            await appContext().state.set({ msalClient: msal.msalClient });
            await msal.login(this.state.email);
            this.props.navigate('/login/success');
        }
        catch (error)
        {
            console.log(error);
        }
    }

    private async loginWithOkta(identityProvider: IIdentityProvider): Promise<void>
    {
        if (!identityProvider.clientId || !identityProvider.tenantDomain)
        {
            return;
        }

        const config =
        {
            issuer: "https://" + identityProvider.tenantDomain,
            clientId: identityProvider.clientId,
            redirectUri: window.location.origin + "/implicit/callback",
            scopes: ['openid', 'email', 'profile'],
        };

        const authClient = new OktaAuth(config);

        await authClient.signInWithRedirect({
            extraParams: {
                login_hint: this.state.email,
                prompt: 'login',
            }
        });
    }

    private async loginWithAuth0(identityProvider: IIdentityProvider): Promise<void>
    {
        if (!identityProvider.clientId || !identityProvider.tenantDomain)
        {
            return;
        }
        const auth0 = appContext().auth0Provider;
        await auth0.initialise(identityProvider.tenantDomain, identityProvider.clientId);
        await auth0.login(this.state.email);
        this.props.navigate('/login/success');
    }

    private async handleNativeLogin(): Promise<void>
    {
        const { email, password } = this.state;
        const isEmailValid = this.validateEmail(email);
        const isPasswordValid = this.validatePassword(password);
        const isValid = isEmailValid && isPasswordValid;

        if (!isValid)
        {
            return;
        }

        try
        {
            const response = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.authenticate.post<IAuthenticationResponse>({
                tenancy: appContext().tenantId,
                body:
                {
                    Username: email,
                    Password: password,
                }
            });
            appContext().authentication.setToken(response.Token);

            const user = appContext().localStorageProvider.getUserDetails();
            appContext().localStorageProvider.setUserDetails({
                ...user,
                email: response.UserName,
                displayName: response.DisplayName,
                userType: 'Native',
                providerType: 'Native',
            });

            this.props.navigate('/login/success');
        }
        catch (error: any)
        {
            this.setState({ passwordError: error.response.data.message });
        }
    }

    private async handleNewPasswordSubmit(): Promise<void>
    {
        const emailIsValid = this.validateEmail(this.state.email);
        if (!emailIsValid)
        {
            return;
        }
        const res = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.byUserid.resetpassword.put<string>({
            tenancy: appContext().tenantId,
            userid: this.state.email,
        });
        this.setState({
            showResetPasswordResult: true,
            resetPasswordMessage: res,
        });
    }

    public render(): JSX.Element
    {
        return (
            <>
                <Box
                    className='login-page'
                    style={{
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        height: '100vh',
                        padding: 'var(--spacing-sd-system-dimension-spacing-three-extra-large) var(--spacing-sd-system-dimension-spacing-none)',
                        backgroundColor: 'var(--colors-sd-system-color-component-surface-container-dim)',
                    }}>
                    <div className='login-page__container'>
                        <div className='login-page__container-left'>
                            <GridContainer
                                style={{ padding: 'var(--spacing-sd-system-dimension-spacing-none) var(--spacing-sd-system-dimension-spacing-extra-small)' }}
                            >
                                <GridItem
                                    compact={5}
                                >
                                    <img style={{ height: '86px' }} src={`/images/Logos/fadx-logo.png`} alt="FADX logo" />
                                </GridItem>
                                <GridItem
                                    compact={7}
                                >
                                    <Typography role='title' size='large'>
                                        {this.labels.Platform_Login_PageTitle}
                                    </Typography>
                                    <Typography role='body'>
                                        {this.labels.Platform_Login_PageSubTitle}
                                    </Typography>
                                </GridItem>
                                <GridItem
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        padding: 'var(--spacing-sd-system-dimension-spacing-none)',
                                        gap: 'var(--spacing-sd-system-dimension-spacing-small)',
                                    }}
                                >
                                    {this.renderPanel()}
                                </GridItem>
                            </GridContainer>
                        </div>
                        {
                            this.state.screenSize == 'expanded' &&
                            <Box
                                style={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <img style={{ height: '450px', width: '565px' }} src={LoginSplash} alt="Meeting location in office next to the window" />
                            </Box>
                        }
                    </div>
                    <IbssFooter
                        screenSize={this.state.screenSize}
                        showLanguageButton={true}
                    />


                </Box >
                {this.renderPrivacyPolicyDialog()}
                <AlertDialog
                    show={this.state.showLoginFailed}
                    title={this.labels.Platform_Login_LoginFailed}
                    message={this.labels.Platform_Login_UnableToLoginMessage}
                    onClose={() => this.setState({ showLoginFailed: false })}
                />
                <AlertDialog
                    show={this.state.showResetPasswordResult}
                    title={this.labels.Platform_Login_ResetPassword}
                    message={this.state.resetPasswordMessage}
                    onClose={() => this.setState({ showResetPasswordResult: false })}
                />
            </>
        );
    }

    private renderPanel(): JSX.Element
    {
        switch (this.state.panel)
        {
            case 'login':
                return this.renderLogin();
            case 'identity-provider-selection':
                return this.renderIdentityProviders();
            case 'forgotten-password':
                return this.renderForgottenPassword();
            default:
                throw new Error('Not supported.');
        }
    }

    private renderLogin(): JSX.Element
    {
        return (
            <GridContainer
                spacing="md"
            >
                <GridItem
                    compact={12}
                    style={{ marginLeft: 'var(--spacing-sd-system-dimension-spacing-two-extra-large)' }}
                >
                    <Typography role='body'>
                        {this.labels.Platform_Login_SignInText}
                    </Typography>
                </GridItem>
                <GridItem
                    compact={12}
                >
                    {
                        this.renderInput(
                            <EmailIcon />,
                            <TextField
                                id='email'
                                name='email'
                                autoComplete='email'
                                autoFocus
                                label={this.labels.Platform_Login_Email}
                                placeholder={this.labels.Platform_Login_Email.toLowerCase()}
                                required={true}
                                type="email"
                                onChange={(event) => this.handleEmailChange(event)}
                                value={this.state.email}
                                invalid={!!this.state.emailError}
                                invalidMessage={this.state.emailError}
                            />)
                    }
                </GridItem>
                {
                    this.state.showPassword &&
                    <GridItem
                        compact={12}
                    >
                        {
                            this.renderInput(
                                <LockIcon />,
                                <PasswordField
                                    id="password"
                                    name="password"
                                    autoComplete='current-password'
                                    label={this.labels.Platform_Login_Password}
                                    placeholder={this.labels.Platform_Login_Password.toLowerCase()}
                                    required={true}
                                    onChange={(event) => this.handlePasswordChange(event)}
                                    value={this.state.password}
                                    invalid={!!this.state.passwordError}
                                    invalidMessage={this.state.passwordError}
                                />
                            )
                        }
                    </GridItem>
                }
                {
                    this.state.showPassword ?
                        <>
                            <GridItem
                                compact={12}
                            >
                                <Button
                                    id='login'
                                    name='login'
                                    styleType='filled'
                                    style={{ width: '100%' }}
                                    onClick={() => this.handleNativeLogin()}
                                >
                                    {this.labels.Platform_Login_Login}
                                </Button>
                            </GridItem>
                            <GridItem
                                compact={12}
                            >
                                <Button
                                    id='login'
                                    name='login'
                                    styleType='ghost'
                                    style={{ width: '100%' }}
                                    onClick={() => this.setState({ panel: 'forgotten-password' })}
                                >
                                    {this.labels.Platform_Login_ForgottenPassword}
                                </Button>
                            </GridItem>
                        </>
                        :
                        <GridItem
                            compact={12}
                        >
                            <Button
                                id='login'
                                name='login'
                                styleType='filled'
                                style={{ width: '100%' }}
                                onClick={() => this.handleLoginClick()}
                            >
                                {this.labels.Platform_CommonAction_Next}
                            </Button>
                        </GridItem>
                }
            </GridContainer>
        );
    }

    private renderIdentityProviders(): JSX.Element
    {
        return (
            <GridContainer
                spacing="md"
            >
                <GridItem
                    compact={12}
                    style={{ marginLeft: 'var(--spacing-sd-system-dimension-spacing-two-extra-large)' }}
                >
                    <Typography role='body'>
                        {this.labels.Platform_Login_ContinueToLogin}
                    </Typography>
                </GridItem>
                <GridItem
                    compact={12}
                >
                    {
                        this.renderInput(
                            <EmailIcon />,
                            <TextField
                                id='email'
                                name='email'
                                autoComplete='email'
                                autoFocus
                                label={this.labels.Platform_Login_Email}
                                placeholder={this.labels.Platform_Login_Email.toLowerCase()}
                                required={true}
                                type="email"
                                onChange={(event) => this.handleEmailChange(event)}
                                value={this.state.email}
                                invalid={!!this.state.emailError}
                                invalidMessage={this.state.emailError}
                            />)
                    }
                </GridItem>
                {
                    this.state.identityProviders.map(i =>
                        <GridItem
                            compact={12}
                        >
                            <Button
                                key={(i.id).toString()}
                                id={(i.id).toString()}
                                styleType="filled"
                                style={{ width: '100%' }}
                                onClick={() => this.handleIdentityProviderClick(i)}
                            >
                                {i.identityProviderName}
                            </Button>
                        </GridItem>
                    )
                }
            </GridContainer>
        );
    }

    private renderForgottenPassword(): JSX.Element
    {
        return (
            <GridContainer
                spacing="md"
            >
                <GridItem
                    compact={12}
                    style={{ marginLeft: 'var(--spacing-sd-system-dimension-spacing-two-extra-large)' }}
                >
                    <Typography role='body'>
                        {this.labels.Platform_Login_RequestNewPassword}
                    </Typography>
                </GridItem>
                <GridItem
                    compact={12}
                >
                    {
                        this.renderInput(
                            <EmailIcon />,
                            <TextField
                                id='email'
                                name='email'
                                autoComplete='email'
                                autoFocus
                                label={this.labels.Platform_Login_Email}
                                placeholder={this.labels.Platform_Login_Email.toLowerCase()}
                                required={true}
                                type='email'
                                onChange={(event) => this.handleEmailChange(event)}
                                value={this.state.email}
                                invalid={!!this.state.emailError}
                                invalidMessage={this.state.emailError}
                            />
                        )
                    }
                </GridItem>
                <GridItem
                    compact={12}
                >
                    <Button
                        id='request-new-password'
                        name='request-new-password'
                        styleType="filled"
                        style={{ width: '100%' }}
                        onClick={() => this.handleNewPasswordSubmit()}
                    >
                        {this.labels.Platform_CommonAction_Submit}
                    </Button>
                </GridItem>
                <GridItem
                    compact={12}
                >
                    <Button
                        id='request-new-password'
                        name='request-new-password'
                        styleType="ghost"
                        style={{ width: '100%' }}
                        onClick={() => this.setState({ panel: 'login' })}
                    >
                        {this.labels.Platform_CommonAction_Back}
                    </Button>
                </GridItem>
            </GridContainer>
        );
    }

    private renderPrivacyPolicyDialog(): JSX.Element
    {
        return (
            <Dialog
                isOpen={this.state.showPrivacyPolicyDialog}
                width={4}
            >
                <DialogContent>
                    <GridContainer>
                        <GridItem>
                            <img
                                style={{ height: '86px' }}
                                src={`/images/Logos/fadx-logo.png`}
                                alt='FADX logo'
                            />
                        </GridItem>
                        <GridItem>
                            <Card
                                title={this.labels.Platform_Login_WelcomeToHub}
                                subTitle={this.labels.Platform_Login_PolicySubTitle}
                            >
                                <GridContainer>
                                    <GridItem>
                                        <Box
                                            tabIndex={0}
                                            aria-label="privacy policy and license"
                                            ref={this.boxRef}
                                            className="border-theme-light rounded text-justify my-3 login-page__privacy-policy"
                                            onScroll={e => this.handlePrivacyPolicyScroll(e)}
                                            style={{
                                                height: "calc(100vh - 475px)",
                                                overflow: 'auto',
                                                padding: 1,
                                            }}
                                            dangerouslySetInnerHTML={{ __html: this.state.privacyPolicyContent }}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <Typography role="body" size="medium">
                                            Consent below to our <a href="#policy" className="primary-link">Privacy Policy</a> and <a href="#license" className="primary-link">EULA</a>.
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            </Card>
                        </GridItem>
                    </GridContainer>
                </DialogContent>
                <DialogFooter>
                    <ButtonBar>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.handlePrivacyPolicyDecline()}
                                disabled={!this.state.enablePrivacyPolicyButtons}
                            >
                                {this.labels.Platform_CommonAction_Decline}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handlePrivacyPolicyAccept()}
                                disabled={!this.state.enablePrivacyPolicyButtons}
                            >
                                {this.labels.Platform_CommonAction_Accept}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </DialogFooter>
            </Dialog>
        );
    }

    private renderInput(icon: ReactNode, input: ReactNode): JSX.Element
    {
        return (
            <GridContainer>
                <GridItem
                    compact={1}
                    style={{ marginTop: 'var(--spacing-sd-system-dimension-spacing-three-extra-large)' }}
                >
                    {icon}
                </GridItem>
                <GridItem
                    compact={11}
                >
                    {input}
                </GridItem>
            </GridContainer>
        );
    }
}

export default withRouting(Login);
export interface IProps extends IRouterProps { }

export interface IState
{
    email: string;
    emailError: string;
    showPassword: boolean;
    password: string;
    passwordError: string;
    panel: Panel;
    identityProviders: IIdentityProvider[];
    showPrivacyPolicyDialog: boolean;
    enablePrivacyPolicyButtons: boolean;
    privacyPolicyContent: string;
    screenSize: ScreenSize;
    showLoginFailed: boolean;
    showResetPasswordResult: boolean;
    resetPasswordMessage: string;
}

export interface IIdentityProvider
{
    clientId: string;
    id: number;
    identityProviderName: string;
    securityProviderTypeId?: SecurityProviderEnum;
    tenantDomain: string;
    tenancyId: string;
}

export type Panel = 'login' | 'identity-provider-selection' | 'forgotten-password';
